import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from './ForgeButton'
import { HierarchyPicker } from './HierarchyPicker'
import { Icon } from './Icon'
import { TierGlyph } from './TierGlyph'
import { ROOT_ID } from '@/features/hierarchy/api'
import { cn } from '@/lib/cn'

/**
 * One filter over the whole estate: a site, a line or an asset.
 *
 * ── The same "choose a place" the user editor uses ───────────────────────
 * A button that opens `HierarchyPicker` in its `single` mode: one generation at
 * a time, searched and paged by the server, with a trail saying where you
 * stand. One control for choosing a place, wherever a place is chosen, rather
 * than a second picker that would drift from the first.
 *
 * That also keeps this off `GET /assets/hierarchy`. The whole-tree read is being
 * withdrawn (see `features/hierarchy/estate.js`), and a filter that needed every
 * node in the account before it could open would go with it.
 *
 * ── The root is a stand-in, never sent ───────────────────────────────────
 * The picker opens on a node above the sites, and choosing it means "every
 * location" — no filter. It carries `ROOT_ID` rather than the account's real id
 * because the real id comes from `GET /account`, which needs `account.manage`,
 * and somebody reading anomalies need not hold it. The picker asks for sites with
 * no parent filter at the enterprise level, so the id is never used.
 *
 * ── The value carries its trail ──────────────────────────────────────────
 * `{id, tier, name, trail}`. The trail is where the place was chosen from: the
 * trigger names the parent from it, so "Line 1" is never ambiguous between two
 * sites, and the picker reopens there instead of at the top.
 *
 * ── A place that MUST be chosen ─────────────────────────────────────────
 * A sensor chart attaches to exactly one plant, line or machine, never to the
 * account. `allowTiers` passes straight through to the picker, which keeps the
 * root row navigable but not choosable; `label`, `emptyLabel`, `title` and
 * `note` let that caller say so. Every one defaults to the filter's own copy, so
 * the anomaly list and the other filters are unchanged.
 *
 * @param {object} props
 * @param {string} props.id
 * @param {?{id: string, tier: string, name: string, trail?: Array}} props.value
 * @param {(place: ?{id: string, tier: string, name: string, trail: Array}) => void} props.onChange
 * @param {Set<string>} [props.allowTiers]
 * @param {string} [props.label]
 * @param {string} [props.emptyLabel]
 * @param {string} [props.title]
 * @param {string} [props.note]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showLabel]   The `Label:` prefix inside the button. Off
 *                                      inside `PlaceField`, which prints a real
 *                                      label above it instead.
 * @param {string} [props.describedBy]  `aria-describedby` — `PlaceField`'s hint
 *                                      and error.
 * @param {boolean} [props.invalid]
 * @param {string} [props.className]
 * @param {?{id: string, tier: string, name: string}} [props.within]  Open the
 *                                      picker on this node instead of the
 *                                      account, so nothing above it can be
 *                                      reached — a plant-owned document's sensor
 *                                      machine, for one.
 * @param {boolean} [props.labelAbove]  Print `label` as a real label ABOVE the
 *                                      button, as `ForgeSelect` and `Field` do,
 *                                      instead of the `Label:` prefix inside it.
 *                                      For a filter row whose other controls
 *                                      are labelled that way.
 */
export function PlaceFilter({
  id,
  value,
  onChange,
  allowTiers,
  label,
  emptyLabel,
  title,
  note,
  disabled = false,
  showLabel = true,
  describedBy,
  invalid = false,
  className,
  labelAbove = false,
  within = null,
}) {
  const { t } = useTranslation(['app', 'common'])
  const [picking, setPicking] = useState(false)

  const root = within
    ? { parentId: null, code: null, isActive: true, ...within }
    : {
        id: ROOT_ID,
        tier: 'enterprise',
        parentId: null,
        name: t('placeFilter.all'),
        code: null,
        isActive: true,
      }

  const parent = value?.trail?.at(-1)
  const parentName = parent && parent.tier !== 'enterprise' ? parent.name : null

  const button = (
    <ForgeButton
      id={id}
      aria-haspopup="dialog"
      aria-expanded={picking}
      aria-describedby={describedBy || undefined}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      onClick={() => setPicking(true)}
      /* The name and its parent truncate at 22rem; the full pair on hover. */
      title={value ? [value.name, parentName].filter(Boolean).join(' · ') : undefined}
      className={cn(
        'max-w-[22rem]',
        value && 'border-forge/40 text-on-forge', invalid && 'border-red',
        /* Beside a toolbar `ForgeSelect`: its 34px at every width and its
           13px regular, so the row reads as one set of controls. */
        labelAbove && 'px-3 text-body-sm font-normal max-lg:min-h-[34px]',
        className,
      )}
    >
      <TierGlyph tier={value?.tier ?? 'enterprise'} bare />
      {showLabel && !labelAbove ? (
        <span className="font-normal text-ink-dim">
          {label ?? t('placeFilter.label')}
          <span className="mx-1 text-ink-mute">:</span>
        </span>
      ) : null}
      <span className="min-w-0 truncate">{value ? value.name : (emptyLabel ?? t('placeFilter.all'))}</span>
      {parentName ? <span className="min-w-0 truncate font-normal text-ink-dim">· {parentName}</span> : null}
      <Icon name="down" size="xs" className="shrink-0" />
    </ForgeButton>
  )

  return (
    <>
      {labelAbove ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={id} className="text-control text-ink">
            {label ?? t('placeFilter.label')}
          </label>
          {button}
        </div>
      ) : (
        button
      )}

      {picking ? (
        <HierarchyPicker
          open
          single
          onClose={() => setPicking(false)}
          title={title ?? t('placeFilter.title')}
          note={note ?? t('placeFilter.scopeNote')}
          allowTiers={allowTiers}
          root={root}
          /* A trail from outside `within` would let the picker stand above it. */
          initialTrail={within && value?.trail?.[0]?.id !== within.id ? undefined : value?.trail}
          value={[value ?? root]}
          onChange={([node], trail) =>
            onChange(
              node.tier === 'enterprise'
                ? null
                : { id: node.id, tier: node.tier, name: node.name, trail },
            )
          }
        />
      ) : null}
    </>
  )
}
